import { useEffect, useState } from "react";
import { Volume2, RefreshCcw, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { speak } from "@/lib/tts";
import { SYLLABLE_ITEMS } from "@/lib/syllableData";
import { randomFrom } from "@/lib/gameData";
import { GAME_FEEDBACK_MS, GAME_SUCCESS_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function nextItem(current) {
  let pick = randomFrom(SYLLABLE_ITEMS);
  let guard = 5;
  while (current && pick.word === current.word && guard-- > 0) pick = randomFrom(SYLLABLE_ITEMS);
  return pick;
}

// Insert a "|" at each division index and return array of syllables
function splitAt(word, positions) {
  const sorted = [...positions].sort((a, b) => a - b);
  const parts = [];
  let start = 0;
  sorted.forEach((p) => {
    parts.push(word.slice(start, p));
    start = p;
  });
  parts.push(word.slice(start));
  return parts.filter(Boolean);
}

export default function SyllableDivision() {
  const [item, setItem] = useState(() => nextItem(null));
  const [selected, setSelected] = useState([]); // positions the user has chosen
  const [result, setResult] = useState(null); // 'correct' | 'wrong' | null
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const { award } = useAuth();

  useEffect(() => {
    setSelected([]);
    setResult(null);
    const t = setTimeout(() => speak(item.word, { rate: 0.9 }), 200);
    return () => clearTimeout(t);
  }, [item]);

  const toggleGap = (pos) => {
    if (result) return;
    setSelected((cur) =>
      cur.includes(pos) ? cur.filter((p) => p !== pos) : [...cur, pos]
    );
  };

  const check = () => {
    if (result) return;
    setAttempts((a) => a + 1);
    const correctPositions = [...item.divisions].sort((a, b) => a - b);
    const chosen = [...selected].sort((a, b) => a - b);
    const isCorrect =
      chosen.length === correctPositions.length &&
      chosen.every((p, i) => p === correctPositions[i]);
    if (isCorrect) {
      setResult("correct");
      setScore((s) => s + 1);
      award(10, "syllable");
      toast.success(`Yes! ${item.pattern} — ${item.note}`);
      const parts = splitAt(item.word, item.divisions);
      // Say each syllable then the whole word
      parts.forEach((p, i) => setTimeout(() => speak(p, { rate: 0.85 }), i * 600));
      setTimeout(() => speak(item.word, { rate: 0.9 }), parts.length * 600 + 300);
      setTimeout(() => setItem((cur) => nextItem(cur)), GAME_SUCCESS_MS + 800);
    } else {
      setResult("wrong");
      toast.error(`Not quite — try again.`);
      setTimeout(() => setResult(null), GAME_FEEDBACK_MS);
    }
  };

  const reset = () => {
    if (result === "correct") return;
    setSelected([]);
    setResult(null);
  };

  const parts = splitAt(item.word, selected);
  const letters = item.word.split("");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div
          data-testid="syllable-score"
          className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Score: {score} / {attempts}
        </div>
        <button
          data-testid="syllable-next"
          onClick={() => setItem((cur) => nextItem(cur))}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New word
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-6 sm:p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
          Tap between letters to split the word into syllables
        </p>
        <button
          data-testid="syllable-play-word"
          onClick={() => speak(item.word, { rate: 0.9 })}
          className="mt-1 mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border-2 border-slate-200 hover:border-teal-400 text-slate-700 font-bold text-sm transition-colors"
        >
          <Volume2 className="w-4 h-4" /> Hear the word
        </button>

        <div
          className={`flex justify-center items-center ${result === "wrong" ? "phonx-shake" : ""}`}
          data-testid="syllable-word-container"
        >
          {letters.map((letter, i) => (
            <div key={`ltr-${i}`} className="flex items-center">
              <div
                className={`font-display text-5xl sm:text-6xl font-semibold px-1
                  ${result === "correct" ? "text-emerald-600" : "text-slate-800"}
                `}
              >
                {letter}
              </div>
              {i < letters.length - 1 && (
                <button
                  data-testid={`syllable-gap-${i + 1}`}
                  onClick={() => toggleGap(i + 1)}
                  disabled={!!result}
                  aria-label={`Split after letter ${i + 1}`}
                  className={`mx-0.5 sm:mx-1 h-14 sm:h-16 w-2 rounded-full transition-all
                    ${selected.includes(i + 1)
                      ? "bg-teal-500 w-2 sm:w-2.5 shadow-md scale-110"
                      : "bg-slate-200 hover:bg-teal-300 hover:w-2.5"
                    }
                  `}
                />
              )}
            </div>
          ))}
        </div>

        {result === "correct" && (
          <div className="mt-5">
            <div className="font-display text-2xl sm:text-3xl font-semibold text-emerald-600">
              {parts.join(" · ")}
            </div>
            <p className="mt-2 text-sm text-slate-600">
              <span className="font-bold text-teal-700">{item.pattern}</span> &mdash; {item.note}
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-center gap-3">
        <button
          data-testid="syllable-reset"
          onClick={reset}
          className="rounded-full px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 font-bold hover:border-slate-400 transition-colors inline-flex items-center gap-1.5"
        >
          <RotateCcw className="w-4 h-4" /> Clear
        </button>
        <button
          data-testid="syllable-check"
          onClick={check}
          disabled={selected.length === 0 || !!result}
          className="rounded-full px-8 py-3 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-200 text-white font-bold shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2"
        >
          <Check className="w-5 h-5" /> Check split
        </button>
      </div>
    </div>
  );
}
